import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Prism from "prismjs";
import "prismjs/themes/prism.css";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-yaml";
import "prismjs/components/prism-json";

import {
  EditorContainer,
  LineNumbers,
  LineNumber,
  EditorWrapper,
  CodeEditor,
  Pre,
  Code,
  TextArea,
} from "./styles";

const SimpleTextEditor = ({
  language = "text",
  height,
  width,
  initialValue = "",
  value,
  onChange,
}) => {
  const [content, setContent] = useState(
    value !== undefined ? value : initialValue
  );
  const [lines, setLines] = useState((content?.split("\n") || []).length || 1);
  const [highlightedCode, setHighlightedCode] = useState("");

  const textareaRef = useRef(null);
  const codeEditorRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    if (value !== undefined && value !== content) {
      setContent(value);
    }
  }, [value]);

  useEffect(() => {
    // Force Prism to re-highlight on theme changes
    if (theme) {
      if (content) highlightCode(content);
    }
  }, [theme, content]);

  useEffect(() => {
    const lineCount = content ? content.split("\n").length : 1;
    setLines(lineCount);
    highlightCode(content);
  }, [content, language]);

  useEffect(() => {
    const textarea = textareaRef.current;
    const codeEditor = codeEditorRef.current;

    if (textarea && codeEditor) {
      const syncScroll = () => {
        codeEditor.scrollTop = textarea.scrollTop;
        codeEditor.scrollLeft = textarea.scrollLeft;
      };

      textarea.addEventListener("scroll", syncScroll);
      return () => textarea.removeEventListener("scroll", syncScroll);
    }
  }, []);

  const highlightCode = (code) => {
    if (!code) {
      setHighlightedCode("");
      return;
    }

    let grammar;
    let languageClass;

    switch (language) {
      case "xml":
        grammar = Prism.languages.markup;
        languageClass = "language-markup";
        break;
      case "html":
        grammar = Prism.languages.markup;
        languageClass = "language-markup";
        break;
      case "yaml":
        grammar = Prism.languages.yaml;
        languageClass = "language-yaml";
        break;
      case "json":
        grammar = Prism.languages.json;
        languageClass = "language-json";
        break;
      default:
        // For plain text, escape HTML characters but don't apply highlighting
        setHighlightedCode(
          `<span>${code
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")}</span>`
        );
        return;
    }

    try {
      const highlighted = Prism.highlight(code, grammar, languageClass);
      setHighlightedCode(highlighted);
    } catch (error) {
      console.error("Syntax highlighting error:", error);
      // Fallback to plain text if highlighting fails
      setHighlightedCode(
        `<span>${code
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")}</span>`
      );
    }
  };

  const handleContentChange = (e) => {
    const newValue = e.target.value;
    setContent(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleKeyDown = (e) => {
    // Intercept Tab to insert spaces instead of moving focus
    if (e.key === "Tab") {
      e.preventDefault();
      const { selectionStart, selectionEnd } = e.target;
      const newContent =
        content.substring(0, selectionStart) +
        "  " +
        content.substring(selectionEnd);

      setContent(newContent);
      if (onChange) {
        onChange(newContent);
      }

      setTimeout(() => {
        textareaRef.current.selectionStart = selectionStart + 2;
        textareaRef.current.selectionEnd = selectionStart + 2;
      }, 0);
    }
  };

  const renderLineNumbers = () => {
    return Array.from({ length: lines }, (_, i) => (
      <LineNumber key={i}>{i + 1}</LineNumber>
    ));
  };

  return (
    <EditorContainer width={width} height={height}>
      <LineNumbers>{renderLineNumbers()}</LineNumbers>
      <EditorWrapper>
        <CodeEditor ref={codeEditorRef}>
          <Pre>
            <Code dangerouslySetInnerHTML={{ __html: highlightedCode }} />
          </Pre>
          <TextArea
            ref={textareaRef}
            value={content}
            onChange={handleContentChange}
            onKeyDown={handleKeyDown}
            spellCheck="false"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            data-gramm="false"
          />
        </CodeEditor>
      </EditorWrapper>
    </EditorContainer>
  );
};

SimpleTextEditor.propTypes = {
  language: PropTypes.oneOf(["xml", "html", "text", "yaml", "json"]),
  height: PropTypes.string,
  width: PropTypes.string,
  initialValue: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default SimpleTextEditor;
